import { useSnackbar } from "notistack"
import { useI18n } from "../../hooks"
import * as reminderApi from "../api"
import { unknownErrorToString } from "../../utils/errors"

export const useLinkMutations = () => {
  const translations = useTranslations()
  const { enqueueSnackbar } = useSnackbar()

  const handleError = (error: any) => {
    const errorMessage = unknownErrorToString(error)
    enqueueSnackbar(errorMessage, { variant: "error" })
  }

  const [createLinkMutation] = reminderApi.useCreateLinkMutation()
  const [updateLinkMutation] = reminderApi.useUpdateLinkMutation()
  const [deleteLinkMutation] = reminderApi.useDeleteLinkMutation()

  const createLink = (newLinkData: reminderApi.NewLinkData) => {
    return createLinkMutation(newLinkData)
      .unwrap()
      .then(() => true)
      .catch(() => handleError({ message: translations.linkCreationFailed }))
  }

  const updateLink = (
    linkId: string,
    linkUpdateData: reminderApi.LinkUpdateData,
    entity: reminderApi.GetLinksQuery
  ) => {
    return updateLinkMutation({ linkId, ...linkUpdateData, ...entity })
      .unwrap()
      .catch(handleError)
  }

  const deleteLink = (linkId: string, entity: reminderApi.GetLinksQuery) => {
    return deleteLinkMutation({ linkId, ...entity })
      .unwrap()
      .then(() => {
        enqueueSnackbar(translations.linkDeletionSuccessful, { variant: "success" })
      })
      .catch(() => handleError({ message: translations.linkDeletionFailed }))
  }

  return { createLink, updateLink, deleteLink }
}

const defaultTranslations = {
  linkCreationFailed: "Failed to add a new link",
  linkDeletionSuccessful: "Link deleted successfully",
  linkDeletionFailed: "Failed to delete link",
}

const useTranslations = (defaults = defaultTranslations) => {
  const { t } = useI18n("translation")

  return {
    linkCreationFailed: t("linkCreationFailed", defaults.linkCreationFailed),
    linkDeletionSuccessful: t("linkDeletionSuccessful", defaults.linkDeletionSuccessful),
    linkDeletionFailed: t("linkDeletionFailed", defaults.linkDeletionFailed),
  }
}
